import React from 'react';
import { Link } from 'react-router-dom';

const HowItWorks: React.FC = () => {
  return (
    <div className="space-y-8 max-w-5xl mx-auto">
      {/* Hero Section */}
      <div className="text-center py-8">
        <h1 className="text-4xl md:text-5xl font-bold mb-4">How AuctionRinger Works</h1>
        <p className="text-xl text-gray-600">
          Predict auction outcomes, earn Ringer$, and become a master predictor!
        </p>
      </div>

      {/* Step 1: Browse Auctions */}
      <div className="card">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="bg-primary-600 text-white text-2xl font-bold w-12 h-12 rounded-full flex items-center justify-center mb-4">
              1
            </div>
            <h2 className="text-3xl font-bold mb-4">Browse Auctions</h2>
            <p className="text-gray-700 text-lg mb-4">
              Explore upcoming auctions from verified auction houses. View detailed photos, descriptions, and estimated values for each lot.
            </p>
            <Link to="/auctions" className="btn-primary">
              View Auctions →
            </Link>
          </div>
          <div className="bg-gradient-to-br from-primary-100 to-primary-200 rounded-lg p-8 text-center">
            <div className="text-6xl mb-4">🔨</div>
            <p className="font-semibold text-gray-700">Browse hundreds of auction lots</p>
          </div>
        </div>
      </div>

      {/* Step 2: Make Predictions */}
      <div className="card">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div className="order-2 md:order-1 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg p-8 text-center">
            <div className="text-6xl mb-4">🎯</div>
            <p className="font-semibold text-gray-700">Predict the final sale price</p>
          </div>
          <div className="order-1 md:order-2">
            <div className="bg-primary-600 text-white text-2xl font-bold w-12 h-12 rounded-full flex items-center justify-center mb-4">
              2
            </div>
            <h2 className="text-3xl font-bold mb-4">Make Your Predictions</h2>
            <p className="text-gray-700 text-lg mb-4">
              Submit your predicted final price for any lot. The earlier you predict, the higher your potential rewards! Remember: don't go over!
            </p>
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
              <p className="text-sm font-semibold text-yellow-800">💡 Pro Tip:</p>
              <p className="text-sm text-yellow-700">Predictions made 24+ hours early earn bonus multipliers!</p>
            </div>
          </div>
        </div>
      </div>

      {/* Step 3: Earn Ringer$ */}
      <div className="card">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="bg-primary-600 text-white text-2xl font-bold w-12 h-12 rounded-full flex items-center justify-center mb-4">
              3
            </div>
            <h2 className="text-3xl font-bold mb-4">Earn Ringer$</h2>
            <p className="text-gray-700 text-lg mb-4">
              After the auction closes, accurate predictions earn Ringer$! The closer you are, the more you earn.
            </p>
          </div>
          <div className="bg-gradient-to-br from-green-100 to-green-200 rounded-lg p-8 text-center">
            <div className="text-6xl mb-4">💰</div>
            <p className="font-semibold text-gray-700">Win big Ringer$ rewards!</p>
          </div>
        </div>
      </div>

      {/* Scoring System */}
      <div className="card bg-gradient-to-br from-purple-50 to-blue-50">
        <h2 className="text-3xl font-bold mb-6 text-center">How Scoring Works</h2>
        
        <div className="space-y-6">
          {/* Wagering System */}
          <div className="bg-white rounded-lg p-6 border-2 border-primary-500">
            <h3 className="text-xl font-bold mb-3 flex items-center text-primary-600">
              <span className="text-2xl mr-2">💎</span>
              Wagering System - Skin in the Game!
            </h3>
            <p className="text-gray-700 mb-3">
              Make predictions by wagering your Ringer$. The more you wager, the bigger your potential reward!
            </p>
            <div className="bg-blue-50 p-4 rounded">
              <p className="text-sm font-semibold text-blue-800">Example:</p>
              <p className="text-sm text-blue-700">Wager 100 R$ with 5% accuracy = Win 500 R$ (5x multiplier)</p>
              <p className="text-sm text-blue-700 mt-1">But if you're wrong... you lose your 100 R$ wager!</p>
            </div>
          </div>

          {/* Accuracy Multipliers */}
          <div>
            <h3 className="text-xl font-bold mb-4 flex items-center">
              <span className="text-2xl mr-2">🎯</span>
              Accuracy Multipliers
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white rounded-lg p-4 border-2 border-purple-500">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold text-purple-600">Near Perfect (1%)</span>
                  <span className="text-2xl font-bold text-purple-600">10x</span>
                </div>
                <p className="text-sm text-gray-600">Within 1% of actual price</p>
              </div>
              
              <div className="bg-white rounded-lg p-4 border-2 border-green-500">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold text-green-600">Excellent (3%)</span>
                  <span className="text-2xl font-bold text-green-600">7x</span>
                </div>
                <p className="text-sm text-gray-600">Within 3% of actual price</p>
              </div>
              
              <div className="bg-white rounded-lg p-4 border-2 border-blue-500">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold text-blue-600">Very Good (5%)</span>
                  <span className="text-2xl font-bold text-blue-600">5x</span>
                </div>
                <p className="text-sm text-gray-600">Within 5% of actual price</p>
              </div>
              
              <div className="bg-white rounded-lg p-4 border-2 border-yellow-500">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold text-yellow-600">Good (10%)</span>
                  <span className="text-2xl font-bold text-yellow-600">3x</span>
                </div>
                <p className="text-sm text-gray-600">Within 10% of actual price</p>
              </div>
            </div>
          </div>

          {/* Beat the Expert Bonus */}
          <div className="bg-orange-50 border-2 border-orange-300 rounded-lg p-6">
            <h3 className="text-xl font-bold mb-3 flex items-center text-orange-700">
              <span className="text-2xl mr-2">⚡</span>
              Beat the Expert Bonus
            </h3>
            <p className="text-gray-700 mb-3">
              Auctioneers provide an expert estimate range (low to high). If you predict <strong>outside this range</strong> and you're correct, you earn <strong>2x to 4x bonus multipliers</strong>!
            </p>
            <div className="grid md:grid-cols-2 gap-3 mt-3">
              <div className="bg-white p-3 rounded">
                <p className="text-sm font-semibold text-orange-800">Inside Expert Range</p>
                <p className="text-xs text-gray-600">Safer bet, standard multipliers</p>
              </div>
              <div className="bg-white p-3 rounded">
                <p className="text-sm font-semibold text-orange-800">Outside Expert Range</p>
                <p className="text-xs text-gray-600">Riskier, but 2x-4x bonus if correct!</p>
              </div>
            </div>
          </div>

          {/* Additional Bonuses */}
          <div>
            <h3 className="text-xl font-bold mb-4 flex items-center">
              <span className="text-2xl mr-2">🚀</span>
              Bonus Multipliers
            </h3>
            <div className="bg-white rounded-lg p-6">
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded">
                  <div>
                    <span className="font-semibold block">Early Bird Bonus</span>
                    <span className="text-sm text-gray-600">Predict 48+ hours early</span>
                  </div>
                  <span className="text-xl font-bold text-purple-600">+50%</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded">
                  <div>
                    <span className="font-semibold block">Difficulty Bonus</span>
                    <span className="text-sm text-gray-600">Tight expert range (&lt;10% spread)</span>
                  </div>
                  <span className="text-xl font-bold text-blue-600">+50%</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-green-50 rounded">
                  <div>
                    <span className="font-semibold block">Outside Range Bonus</span>
                    <span className="text-sm text-gray-600">Beat the expert estimate</span>
                  </div>
                  <span className="text-xl font-bold text-green-600">2x-4x</span>
                </div>
              </div>
            </div>
          </div>

          {/* The Price is Right Rule */}
          <div className="bg-red-50 border-2 border-red-300 rounded-lg p-6">
            <h3 className="text-xl font-bold mb-2 flex items-center text-red-700">
              <span className="text-2xl mr-2">⚠️</span>
              Critical Rule: Don't Go Over!
            </h3>
            <p className="text-gray-700">
              Like "The Price is Right," if your prediction is <strong>higher than the actual final price</strong>, you <strong>lose your entire wager</strong>. This adds real strategy - do you play it safe or go for maximum rewards?
            </p>
          </div>
        </div>
      </div>

      {/* Example Calculation */}
      <div className="card">
        <h2 className="text-3xl font-bold mb-6 text-center">Example Calculation</h2>
        
        <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-lg p-6 mb-6">
          <div className="text-center mb-6">
            <p className="text-lg font-semibold text-gray-700 mb-2">Scenario: Vintage Baseball Card</p>
            <p className="text-3xl font-bold text-primary-600 mb-2">Final Sale Price: $100</p>
            <p className="text-sm text-gray-600">Expert Range: $80 - $110</p>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {/* Winner */}
            <div className="bg-white rounded-lg p-4 border-2 border-green-500">
              <div className="text-center mb-3">
                <div className="text-4xl mb-2">✅</div>
                <p className="font-bold text-lg">Big Winner!</p>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Prediction:</span>
                  <span className="font-bold">$75</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Wager:</span>
                  <span className="font-bold">100 R$</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Accuracy:</span>
                  <span className="font-bold">75% off by $25</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Outside Range:</span>
                  <span className="font-bold text-orange-600">Yes! (Below $80)</span>
                </div>
                <div className="border-t pt-2 mt-2">
                  <div className="flex justify-between text-xs">
                    <span>Base (5% acc):</span>
                    <span>5x</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span>Beat Expert:</span>
                    <span>2x</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span>Early (48h):</span>
                    <span>1.5x</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold text-green-600 mt-2">
                    <span>Won:</span>
                    <span>1,500 R$</span>
                  </div>
                  <p className="text-xs text-green-600 text-center mt-1">+1,400 R$ profit!</p>
                </div>
              </div>
            </div>

            {/* Went Over */}
            <div className="bg-white rounded-lg p-4 border-2 border-red-500">
              <div className="text-center mb-3">
                <div className="text-4xl mb-2">❌</div>
                <p className="font-bold text-lg">Lost!</p>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Prediction:</span>
                  <span className="font-bold">$105</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Wager:</span>
                  <span className="font-bold">100 R$</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Result:</span>
                  <span className="font-bold text-red-600">WENT OVER</span>
                </div>
                <div className="border-t pt-2 mt-2">
                  <div className="flex justify-between text-lg font-bold text-red-600 mt-2">
                    <span>Lost:</span>
                    <span>100 R$</span>
                  </div>
                  <p className="text-xs text-red-600 mt-2">Predicted $5 too high!</p>
                </div>
              </div>
            </div>

            {/* Safe Bet */}
            <div className="bg-white rounded-lg p-4 border-2 border-blue-500">
              <div className="text-center mb-3">
                <div className="text-4xl mb-2">👍</div>
                <p className="font-bold text-lg">Safe Winner</p>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Prediction:</span>
                  <span className="font-bold">$95</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Wager:</span>
                  <span className="font-bold">100 R$</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Accuracy:</span>
                  <span className="font-bold">5% off</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Inside Range:</span>
                  <span className="font-bold text-blue-600">Yes ($80-$110)</span>
                </div>
                <div className="border-t pt-2 mt-2">
                  <div className="flex justify-between text-xs">
                    <span>Base (5% acc):</span>
                    <span>5x</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold text-blue-600 mt-2">
                    <span>Won:</span>
                    <span>500 R$</span>
                  </div>
                  <p className="text-xs text-blue-600 text-center mt-1">+400 R$ profit</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* What Can You Do With Ringer$ */}
      <div className="card bg-gradient-to-br from-blue-600 to-purple-600 text-white">
        <h2 className="text-3xl font-bold mb-6 text-center">What Can You Do With Ringer$?</h2>
        
        <div className="grid md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-5xl mb-3">🎁</div>
            <h3 className="font-bold mb-2">Real Merchandise</h3>
            <p className="text-sm opacity-90">Redeem for actual collectibles and items</p>
          </div>
          <div className="text-center">
            <div className="text-5xl mb-3">💳</div>
            <h3 className="font-bold mb-2">Gift Cards</h3>
            <p className="text-sm opacity-90">Convert to gift cards from major retailers</p>
          </div>
          <div className="text-center">
            <div className="text-5xl mb-3">🎮</div>
            <h3 className="font-bold mb-2">Tournaments</h3>
            <p className="text-sm opacity-90">Enter special prediction competitions</p>
          </div>
          <div className="text-center">
            <div className="text-5xl mb-3">⭐</div>
            <h3 className="font-bold mb-2">Premium Features</h3>
            <p className="text-sm opacity-90">Unlock advanced analytics and tools</p>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="text-center py-8">
        <h2 className="text-3xl font-bold mb-4">Ready to Become a Ringer?</h2>
        <p className="text-xl text-gray-600 mb-6">
          Start making predictions and earning Ringer$ today!
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/register" className="btn-primary text-lg px-8 py-3">
            Sign Up Free
          </Link>
          <Link to="/auctions" className="btn-secondary text-lg px-8 py-3">
            Browse Auctions
          </Link>
        </div>
      </div>
    </div>
  );
};

export default HowItWorks;